import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';
import * as Sentry from '@sentry/react';
import api, { setAuthToken } from '../utils/api';
import { User } from '../types';
import { getUserProfile } from '../api/Users';

declare global {
  interface Window {
    clarity?: (...args: any[]) => void;
  }
}

/** Identify the current user to Sentry (error attribution) and Microsoft
 * Clarity (session replay) so sessions can be tied back to an account. */
function identifyUser(user: User) {
  Sentry.setUser({ id: user.id, email: user.email });
  if (typeof window.clarity === 'function') {
    window.clarity('identify', user.id, undefined, undefined, user.email);
  }
}

/** Clear identity on logout — subsequent errors/sessions record as anonymous. */
function clearIdentity() {
  Sentry.setUser(null);
}

export interface ImpersonatedUser {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (token: string) => void;
  logout: () => void;
  impersonating: ImpersonatedUser | null;
  startImpersonation: (impersonationToken: string, adminToken: string, impersonatedUser: ImpersonatedUser) => Promise<void>;
  exitImpersonation: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState<ImpersonatedUser | null>(() => {
    const saved = localStorage.getItem('impersonatingUser');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        const decodedToken = jwtDecode<any>(token);
        if (decodedToken.exp * 1000 < Date.now()) {
          logout();
        } else {
          setAuthToken(token);
          try {
            const fullUser = await getUserProfile(decodedToken.user.id);
            setUser(fullUser);
            identifyUser(fullUser);
          } catch (error) {
            console.error("Failed to load user profile", error);
            logout();
          }
        }
      }
      setLoading(false);
    };
    loadUser();
  }, []);

  const login = async (token: string) => {
    localStorage.setItem('token', token);
    setAuthToken(token);
    const decodedToken = jwtDecode<any>(token);
    try {
      const fullUser = await getUserProfile(decodedToken.user.id);
      setUser(fullUser);
      identifyUser(fullUser);
    } catch (error) {
      logout();
      alert('Login failed: Unable to load user profile. Please try again or contact support.');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('impersonatingUser');
    setAuthToken(null);
    setUser(null);
    setImpersonating(null);
    clearIdentity();
  };

  const startImpersonation = async (impersonationToken: string, adminToken: string, impersonatedUser: ImpersonatedUser) => {
    localStorage.setItem('adminToken', adminToken);
    localStorage.setItem('impersonatingUser', JSON.stringify(impersonatedUser));
    setImpersonating(impersonatedUser);
    await login(impersonationToken);
  };

  const exitImpersonation = async () => {
    const adminToken = localStorage.getItem('adminToken');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('impersonatingUser');
    setImpersonating(null);
    if (adminToken) {
      await login(adminToken);
    }
  };

  const value = { user, loading, login, logout, impersonating, startImpersonation, exitImpersonation };

  return (
    <AuthContext.Provider value={value}>
      {/* Don't render children until loading is false */}
      {!loading && children}
    </AuthContext.Provider>
  );
};